<script setup lang="ts">
import { computed } from 'vue';
import { useTranslation } from 'i18next-vue';
import {
  instanceOfTrackItMeasurementParameterAreaDto,
  instanceOfTrackItMeasurementParameterBooleanDto,
  instanceOfTrackItMeasurementParameterDoubleDto,
  instanceOfTrackItMeasurementParameterLongDto,
  instanceOfTrackItMeasurementParameterModalityDto,
  instanceOfTrackItMeasurementPropertyFilterDto,
  Modality,
  ParameterCombinationDto,
  TrackItMeasurementFilterForCombinationDto,
  TrackItMeasurementParameterFilterDto,
  TrackItMeasurementParameterFilterWithUserValueDto,
  TrackItMeasurementPropertyFilterDto,
} from '@/api/generator';
import OverviewTable from '@/components/OverviewTable/OverviewTable.vue';
import Notification from '@/components/notifications/Notification.vue';
import VeeCheckbox from '@/components/forms/Checkbox/VeeCheckbox.vue';
import InlineValidationError from '@/components/forms/InlineValidationError/InlineValidationError.vue';
import VeeNumberField from '@/components/forms/NumberField/VeeNumberField.vue';
import VeeSelect from '@/components/forms/Select/VeeSelect.vue';
import VeeTextField from '@/components/forms/TextField/VeeTextField.vue';
import { useFormMode } from '@/components/forms/useFormMode.ts';
import { trackItLogo } from '@/components/icons/icons';
import { I18nKey } from '@/utils/typings';
import {
  numberFormatOfMeasurementParameter,
  trackItFiltersMatch,
  translateMeasurementPropertyName,
} from './trackItFilters';

const { t } = useTranslation();
const mode = useFormMode();

const props = defineProps<{
  name: string; // the combination's form path, e.g. `taskItemAreas[0].combinationTable.combinations[1]`
  combination: ParameterCombinationDto;
  selectedFilters: TrackItMeasurementParameterFilterDto[];
  selectedProperties: TrackItMeasurementPropertyFilterDto[];
  // The combination itself is disabled: everything is shown greyed out and can't be edited.
  disabled?: boolean;
}>();

const modalityOptions = Object.values(Modality);

const rows = computed(() =>
  props.selectedFilters
    .map((selected) =>
      props.combination.trackItFilters?.find(
        (filter): filter is TrackItMeasurementParameterFilterWithUserValueDto =>
          !instanceOfTrackItMeasurementPropertyFilterDto(filter) &&
          'measurementParameter' in filter &&
          trackItFiltersMatch(filter, selected),
      ),
    )
    .filter(
      (row): row is TrackItMeasurementParameterFilterWithUserValueDto =>
        !!row?.measurementParameter,
    )
    // Read-only: there's nothing to un-ignore, so an ignored row is just noise.
    .filter((row) => mode.value !== 'details' || row.isEnabled),
);

const propertyRows = computed(() =>
  props.selectedProperties
    .map((selected) =>
      props.combination.trackItFilters?.find(
        (filter): filter is TrackItMeasurementPropertyFilterDto =>
          instanceOfTrackItMeasurementPropertyFilterDto(filter) &&
          trackItFiltersMatch(filter, selected),
      ),
    )
    .filter((row): row is TrackItMeasurementPropertyFilterDto => !!row)
    .filter((row) => mode.value !== 'details' || row.isEnabled),
);

// What the tables render: the name as the row's label, the filter itself for the slots.
const parameterItems = computed(() =>
  rows.value.map((filter, index) => ({
    index,
    label: filter.measurementParameter!.name ?? '',
    filter,
  })),
);
const propertyItems = computed(() =>
  propertyRows.value.map((filter, index) => ({
    index,
    label: translateMeasurementPropertyName(filter.measurementProperty!.name),
    filter,
  })),
);

// Read-only shows enabled rows only, so there the checkbox column would be all checked boxes.
// Untitled, as in the setup parameter dialog; the checkboxes carry an aria-label.
const headers = computed(() => [
  ...(mode.value !== 'details'
    ? [{ title: '', key: 'isEnabled', width: 30, sortable: false }]
    : []),
  { title: t('Name'), key: 'label', sortable: false },
  { title: t('Value'), key: 'value', sortable: false },
]);

// Rows are matched by identity, so their form path comes from their position in the combination.
function filterPath(row: TrackItMeasurementFilterForCombinationDto) {
  return `${props.name}.trackItFilters[${props.combination.trackItFilters?.indexOf(row)}]`;
}

function isRowDisabled(row: TrackItMeasurementFilterForCombinationDto) {
  return props.disabled || !row.isEnabled;
}

function unitOf(row: TrackItMeasurementParameterFilterWithUserValueDto): string | undefined {
  const param = row.measurementParameter;
  if (!param) return undefined;
  if (instanceOfTrackItMeasurementParameterAreaDto(param)) return param.unit ?? undefined;
  if (instanceOfTrackItMeasurementParameterDoubleDto(param)) return param.unit ?? undefined;
  if (instanceOfTrackItMeasurementParameterLongDto(param)) return param.unit ?? undefined;
  return undefined;
}
</script>

<template>
  <!-- The same container as "Setup parameters" of the QA equipment setup: level 3 with a grey title
       bar. -->
  <v-card class="container-level-3">
    <v-card-item>
      <v-card-title class="align-center">
        <v-icon color="on-off-white" :size="20" :icon="trackItLogo" class="mr-2" />
        <h6>
          {{ $t('TrackItSetup' as I18nKey) }}
        </h6>
      </v-card-title>
    </v-card-item>
    <v-card-text>
      <!-- Nothing selected in "Track-it data" yet: say what to do instead of hiding the card. -->
      <Notification
        v-if="!rows.length && !propertyRows.length"
        :value="{
          type: 'info',
          message: $t('TrackItSetupEmptyMessage' as I18nKey),
          closable: false,
        }"
      />
      <v-row v-else>
        <v-col v-if="rows.length">
          <div>
            <h6 class="text-primary">{{ $t('TrackItMeasurementParameter_other' as I18nKey) }}</h6>
            <OverviewTable
              class="flat-table"
              :items="parameterItems"
              :headers="headers"
              :zebra="true"
              :noSpacing="true"
              itemValue="index"
              :showItemsCount="false"
            >
              <template #item.isEnabled="{ item: { filter, label } }">
                <VeeCheckbox
                  :name="`${filterPath(filter)}.isEnabled`"
                  :aria-label="label"
                  :disabled="disabled"
                  hideDetails
                  class="mr-4"
                />
              </template>

              <template #item.label="{ item: { filter, label } }">
                <span
                  class="condition-name text-body-1 font-weight-bold"
                  :class="{ 'condition-disabled': isRowDisabled(filter) }"
                >
                  {{ label }}
                  <InlineValidationError
                    :name="filterPath(filter)"
                    variant="icon-only"
                    ignoreTouched
                  />
                </span>
              </template>

              <template #item.value="{ item: { filter } }">
                <div class="d-flex align-center">
                  <VeeCheckbox
                    v-if="
                      instanceOfTrackItMeasurementParameterBooleanDto(filter.measurementParameter!)
                    "
                    :name="`${filterPath(filter)}.measurementParameter.value`"
                    hideDetails
                    class="value-field"
                    :disabled="isRowDisabled(filter)"
                  />

                  <VeeNumberField
                    v-else-if="
                      instanceOfTrackItMeasurementParameterDoubleDto(
                        filter.measurementParameter!,
                      ) ||
                      instanceOfTrackItMeasurementParameterLongDto(filter.measurementParameter!)
                    "
                    :format="numberFormatOfMeasurementParameter(filter.measurementParameter!)"
                    :name="`${filterPath(filter)}.measurementParameter.value`"
                    density="compact"
                    hideDetails="auto"
                    class="value-field"
                    :disabled="isRowDisabled(filter)"
                    :suffix="unitOf(filter)"
                  />

                  <VeeSelect
                    v-else-if="
                      instanceOfTrackItMeasurementParameterModalityDto(filter.measurementParameter!)
                    "
                    :name="`${filterPath(filter)}.measurementParameter.value`"
                    :items="modalityOptions"
                    density="compact"
                    hideDetails="auto"
                    class="value-field"
                    :disabled="isRowDisabled(filter)"
                  />

                  <div
                    v-else-if="
                      instanceOfTrackItMeasurementParameterAreaDto(filter.measurementParameter!)
                    "
                    class="d-flex align-center ga-1"
                  >
                    <VeeNumberField
                      :format="numberFormatOfMeasurementParameter(filter.measurementParameter!)"
                      :name="`${filterPath(filter)}.measurementParameter.valueX`"
                      density="compact"
                      hideDetails="auto"
                      :class="mode === 'details' ? undefined : 'value-field-area'"
                      :disabled="isRowDisabled(filter)"
                    />
                    <span>x</span>
                    <VeeNumberField
                      :format="numberFormatOfMeasurementParameter(filter.measurementParameter!)"
                      :name="`${filterPath(filter)}.measurementParameter.valueY`"
                      density="compact"
                      hideDetails="auto"
                      :class="mode === 'details' ? undefined : 'value-field-area'"
                      :disabled="isRowDisabled(filter)"
                    />
                    <span v-if="unitOf(filter)" class="text-medium-emphasis">
                      {{ unitOf(filter) }}
                    </span>
                  </div>

                  <VeeTextField
                    v-else
                    :name="`${filterPath(filter)}.measurementParameter.value`"
                    density="compact"
                    hideDetails="auto"
                    class="value-field"
                    :disabled="isRowDisabled(filter)"
                  />
                </div>
              </template>
            </OverviewTable>
          </div>
        </v-col>

        <v-col v-if="propertyRows.length">
          <div>
            <h6 class="text-primary">{{ $t('TrackItMeasuringProperty_other' as I18nKey) }}</h6>
            <OverviewTable
              class="flat-table"
              :items="propertyItems"
              :headers="headers"
              :zebra="true"
              :noSpacing="true"
              itemValue="index"
              :showItemsCount="false"
            >
              <template #item.isEnabled="{ item: { filter, label } }">
                <VeeCheckbox
                  :name="`${filterPath(filter)}.isEnabled`"
                  :aria-label="label"
                  :disabled="disabled"
                  hideDetails
                  class="mr-4"
                />
              </template>

              <template #item.label="{ item: { filter, label } }">
                <span
                  class="condition-name text-body-1 font-weight-bold"
                  :class="{ 'condition-disabled': isRowDisabled(filter) }"
                >
                  {{ label }}
                  <InlineValidationError
                    :name="filterPath(filter)"
                    variant="icon-only"
                    ignoreTouched
                  />
                </span>
              </template>

              <template #item.value="{ item: { filter } }">
                <div class="d-flex align-center">
                  <VeeTextField
                    :name="`${filterPath(filter)}.measurementProperty.value`"
                    :placeholder="$t('Required')"
                    density="compact"
                    hideDetails="auto"
                    class="value-field"
                    :disabled="isRowDisabled(filter)"
                  />
                </div>
              </template>
            </OverviewTable>
          </div>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<style scoped lang="scss">
@use '@/styles/mixins';

// One shadow around the whole table instead of the ones of its head, rows and container.
.flat-table.overviewTable {
  @include mixins.elevation(2);

  &:deep(.v-data-table),
  &:deep(.v-data-table table thead),
  &:deep(.v-data-table table tr),
  &:deep(.v-data-table table tr:hover) {
    box-shadow: none !important;
  }
}
:deep(.v-selection-control) {
  min-height: unset;
}
.condition-disabled {
  color: rgb(var(--v-theme-primary-lighten-2));
}
.value-field {
  // Ends at the same edge as the two area fields of "Field size".
  width: 13.25em;
  // v-input defaults to flex: 1 1 auto — the enum v-select (and any field in a row whose
  // sibling column is empty) would stretch far wider than the other value fields otherwise.
  flex: 0 0 auto;
}
.value-field-area {
  width: 6em;
  flex: 0 0 auto;
}
</style>
